import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { designById } from "../lib/designs";

const Ctx = createContext(null);
const VARS = {
  primary: "--c-primary", primary_dark: "--c-primary-dark", primary_light: "--c-primary-light",
  accent: "--c-accent", background: "--c-background", surface: "--c-surface", ink: "--c-ink",
};
const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)).join(" ");

export const applyTheme = (t) => {
  const s = document.documentElement.style;
  Object.entries(VARS).forEach(([k, v]) => s.setProperty(v, rgb(t[k])));
  s.setProperty("--font-heading", `"${t.heading_font}"`);
  s.setProperty("--font-body", `"${t.body_font}"`);
  s.setProperty("--r", `${t.radius}px`);
  let link = document.getElementById("theme-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "theme-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  const fams = [...new Set([t.heading_font, t.body_font])].map((f) => `family=${f.replace(/ /g, "+")}:wght@400;500;600;700`);
  link.href = `https://fonts.googleapis.com/css2?${fams.join("&")}&display=swap`;
};

export const useSettingsQuery = (slug) =>
  useQuery({ queryKey: ["settings", slug], queryFn: () => api.get("/settings").then((r) => r.data), staleTime: 300_000, enabled: !!slug, retry: false });

export const useSitePath = () => {
  const { slug } = useParams();
  return useCallback((p = "/") => `/s/${slug}${p === "/" ? "" : p}`, [slug]);
};

const SOURCES = ["button", "search", "embed", "google", "google_ads"];
export const getSource = (slug) => sessionStorage.getItem(`eres-src-${slug}`) || "direct";

export const useTrackSource = (slug) => {
  const [param] = useState(() => new URLSearchParams(window.location.search).get("src"));
  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).has("design") || (window.self !== window.top && sessionStorage.getItem(`eres-preview-design-${slug}`));
    if (preview || window.location.pathname.endsWith("/widget")) return;
    if (SOURCES.includes(param)) sessionStorage.setItem(`eres-src-${slug}`, param);
    const src = getSource(slug);
    const key = `eres-visit-${slug}-${src}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post("/visits", { source: src }).catch(() => sessionStorage.removeItem(key));
  }, [slug, param]);
};

export const useEmbedHeight = () => {
  useEffect(() => {
    if (window.self === window.top || !window.ResizeObserver) return undefined;
    const el = document.getElementById("root");
    let last = 0;
    const post = () => {
      const pops = [...document.querySelectorAll("[data-radix-popper-content-wrapper]")].map((p) => p.getBoundingClientRect().bottom + window.scrollY + 12);
      const height = Math.ceil(Math.max(el.getBoundingClientRect().height, ...pops));
      if (height === last) return;
      last = height;
      window.parent.postMessage({ type: "eres-booking-height", height }, "*");
    };
    const ro = new ResizeObserver(post);
    ro.observe(el);
    const mo = new MutationObserver(() => requestAnimationFrame(post));
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, []);
};

const previewDesign = (slug) => {
  const q = new URLSearchParams(window.location.search);
  const id = q.get("design");
  const key = `eres-preview-design-${slug}`;
  if (id) sessionStorage.setItem(key, JSON.stringify({ id, keep: q.get("keep") === "1" }));
  return window.self !== window.top || id ? JSON.parse(sessionStorage.getItem(key) || "null") : null;
};

export const SiteProvider = ({ slug, children }) => {
  const { data: raw, isError } = useSettingsQuery(slug);
  const [preview] = useState(() => previewDesign(slug));
  const data = useMemo(() => (raw && preview && designById(preview.id).id === preview.id
    ? { ...raw, design: preview.id, theme: preview.keep ? raw.theme : designById(preview.id).theme } : raw), [raw, preview]);
  useEffect(() => {
    if (data) {
      applyTheme(data.theme);
      document.documentElement.dataset.design = designById(data.design).id;
      document.title = `${data.brand.name} | Book online`;
    }
  }, [data]);
  if (isError) {
    return <div className="grid min-h-screen place-items-center bg-sand p-8 text-center font-sans text-ink" data-testid="site-not-found">
      <div><p className="font-serif text-3xl">Booking site not found</p><p className="mt-2 text-sm text-ink/60">Check the link you were given.</p></div>
    </div>;
  }
  if (!data) return <div className="min-h-screen bg-sand" data-testid="site-loading" />;
  return <Ctx.Provider value={data}>{children}</Ctx.Provider>;
};

export const useSite = () => useContext(Ctx);
export const useDesign = () => designById(useContext(Ctx)?.design).id;
